import { useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';
import LoadingAnimation from 'components/LoadingAnimation';
import { StdList, StdListBody, StdListHeader } from 'components/StdList';
import AppStoreContext from 'contexts/AppStoreContext';
import React, { CSSProperties, useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { EditSubmittal } from 'records/interactions/EditSubmittal';
import { ViewSubmittal } from 'records/interactions/ViewSubmittal';
import { Role } from 'records/Role';
import { Submittal, SubmittalListTypes } from 'records/Submittal';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import SubmittalDeepLink from '../SubmittalDeepLink';
import SubmittalListHeaderCell from '../SubmittalListHeaderCell';
import MySubmittalsRenderer from './MySubmittalsRenderer';
import MySubmittalsSubHeader from './MySubmittalsSubHeader';

const gridTemplateColumns = '120px 90px 1fr 1.2fr 1fr 1fr 1fr';

interface RenderingProps {
  style: CSSProperties;
  index: number;
  data: any;
}

interface StyleProps {
  height: number;
  width: number;
}

interface Props extends WithAppStoreProps, RouteComponentProps {}

const MySubmittalsTab = (props: Props) => {
  const { userId } = useAuth();
  const { appState, actions } = props;
  const [loadingSubmittals, setLoadingSubmittals] = useState<boolean>(false);
  const { translate } = useTranslator(['submittalsPage', 'statusLabels']);

  useEffect(() => {
    loadSubmittals();
    setLoadingSubmittals(true);
    return () => {
      actions.setSelectedSubmittals(
        appState.submittals.keySeq().toSet().clear()
      );
    };
  }, []);

  const loadSubmittals = async (): Promise<void> => {
    await actions.initSubmittalQuery();
    await actions.getSubmittals(SubmittalListTypes.MINE);
    await actions.querySubmittals();
    setLoadingSubmittals(false);
  };

  const handleSubmittalRowClick = (submittal: Submittal): void => {
    const { history, location } = props;

    // If editing another submittal, don't switch to view another submittal.
    if (appState.interactions.some((i) => i.type === 'EditSubmittal')) {
      return;
    }

    if (appState.interactions.some((i) => i.type === 'ViewSubmittal')) {
      const currentInteraction = appState.interactions.find(
        (i) => i.type === 'ViewSubmittal'
      ) as ViewSubmittal;

      if (currentInteraction.submittal.id !== submittal.id) {
        const updatedInteraction = currentInteraction.set(
          'submittal',
          submittal
        );

        actions.updateInteraction(updatedInteraction);
      }

      const nextLocation = {
        ...location,
        search: `submittal=${submittal.id}`,
      };

      history.replace(nextLocation);

      return;
    }

    // If no submittal is being viewed, open a new interaction.
    const interaction = new ViewSubmittal({
      submittal,
    });

    actions.createInteraction(interaction);
  };

  const handleSubmittanlCheckRow = (submittal: Submittal): void => {
    const { selectedSubmittals } = appState;

    const updatedSelectedSubmittals = selectedSubmittals.has(submittal.id)
      ? selectedSubmittals.delete(submittal.id)
      : selectedSubmittals.add(submittal.id);

    actions.setSelectedSubmittals(updatedSelectedSubmittals);
  };

  const getOpenSubmittal = (): string | null => {
    const { interactions } = appState;

    const viewSubInteraction: ViewSubmittal = interactions.find(
      (i) => i.type === 'ViewSubmittal'
    ) as ViewSubmittal;

    if (viewSubInteraction) {
      return viewSubInteraction.submittal.id;
    }

    const editSubInteraction: EditSubmittal = interactions.find(
      (i) => i.type === 'EditSubmittal'
    ) as EditSubmittal;

    if (editSubInteraction) {
      return editSubInteraction.submittal.id;
    }

    return null;
  };

  const { submittals, selectedSubmittals, roles } = appState;
  const { state } = useContext(AppStoreContext);
  const { queriedSubmittals } = state;

  const queryParams = new URLSearchParams(props.location.search);
  const submittalQueryParam = queryParams.get('submittal');

  const normalizedQueriedSubmittals = queriedSubmittals
    .map((id: string): Submittal | undefined => submittals.get(id))
    .filter((s: Submittal | undefined): boolean => {
      return s !== undefined; // && s.ballincourt === currentUserEmail; // TODO: pull this out when dates are fixed
    });

  const openSubmittal = getOpenSubmittal() || '';

  const { role } = roles.get(userId, new Role());
  const isSub = role === 'viewer';

  const itemHeight = 64;

  return (
    <>
      {submittalQueryParam ? (
        <SubmittalDeepLink
          key={submittalQueryParam}
          submittalId={submittalQueryParam}
        />
      ) : null}
      <div>
        {!loadingSubmittals ? (
          <>
            <MySubmittalsSubHeader />
            <div className="mx4 mb4" style={{ height: 'calc(100vh - 200px)' }}>
              <StdList
                id="my-submittals-tab-list"
                gridTemplateColumns={gridTemplateColumns}
              >
                <StdListHeader>
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:submittalNumber')}
                    field="submittalNumber"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:specNumber')}
                    field="specNumber"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:specSection')}
                    field="specSection"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:description')}
                    field="specDescription"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:category')}
                    field="category"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('statusLabels:status')}
                    field="submittalStatus"
                    sortCell
                  />
                  <SubmittalListHeaderCell
                    text={translate('submittalsPage:dueForMe')}
                    field={isSub ? 'dueFromSub' : 'dueFromReviewer'}
                    sortCell
                  />
                </StdListHeader>
                <StdListBody enableScrolling={false}>
                  <AutoSizer>
                    {({ height, width }: StyleProps) => (
                      <FixedSizeList
                        height={height}
                        width={width}
                        itemCount={normalizedQueriedSubmittals.size}
                        itemSize={itemHeight}
                        itemData={normalizedQueriedSubmittals}
                      >
                        {({ style, index, data }: RenderingProps) =>
                          MySubmittalsRenderer(
                            index,
                            style,
                            data,
                            selectedSubmittals,
                            openSubmittal,
                            isSub,
                            handleSubmittalRowClick,
                            handleSubmittanlCheckRow
                          )
                        }
                      </FixedSizeList>
                    )}
                  </AutoSizer>
                </StdListBody>
              </StdList>
            </div>
          </>
        ) : (
          <div
            className="flex-center"
            style={{
              height: 'calc(100vh - 176px)',
            }}
          >
            <LoadingAnimation
              pageName={translate('submittalsPage:loadingSubmittals')}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default withAppStore(MySubmittalsTab);
